import { Tab } from "@headlessui/react";
import { DragDropContext } from "@hello-pangea/dnd";
import { InfiniteQueryObserverBaseResult } from "@tanstack/react-query";
import { PanelLeftClose } from "lucide-react";
import { useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { ConfirmationModal, ConfirmationModalVariant } from "../ConfirmationModal";
import ExperimentModal from "../ExperimentModal";
import { LabelsSelect } from "../LabelsSelect";

import ArchiveGroup from "./ArchiveGroup";
import BacktestModal from "./BacktestModal";
import { CollapsedVersionRail } from "./CollapsedVersionRail";
import {
  useABTestManagement,
  useExperimentModal,
  useInfiniteScroll,
  useLabelDragAndDrop,
  useOptimisticVersions,
  usePendingReleaseLabelMoves,
} from "./hooks";
import useScrollToCurrentVersion from "./hooks/useScrollToCurrentVersion";
import ReleaseLabelConfigurationDialog from "./ReleaseLabelConfigurationDialog";
import { useLabelConfiguration } from "./ReleaseLabelConfigurationDialog/hooks";
import type { VersionSelectorVersion, VersionSelectorVersionId } from "./types";
import VersionSelectorShimmer from "./VersionSelectorShimmer";
import VersionTab from "./VersionTab";

import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { Button } from "@/components/ui/button";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { cn } from "@/lib/utils";
import { useCreateBacktestWithLogs } from "@/queries";
import { EntityType } from "@/queries/folder-entities";
import { useArchivePromptVersion } from "@/queries/prompts";
import { ListPromptVersion } from "@/types";
import { ReportScore } from "@/types/evaluate";
import { displayErrorToast } from "@/utils/toast";

export interface VersionSelectorProps {
  versions?: VersionSelectorVersion[];
  dynamicReleaseLabelsRegistry?: Record<number, { name: string; id: number }[]>;
  versionEvaluationScoresRegistry?: Record<number, ReportScore | null>;
  showVersionList: boolean;
  setShowVersionList: (value: boolean) => void;
  onAddReleaseLabel?: (label: string, versionId: VersionSelectorVersionId) => Promise<unknown>;
  onRemoveReleaseLabel?: (labelId: number) => Promise<unknown>;
  onMoveReleaseLabel?: (
    labelId: number,
    labelName: string,
    targetVersionId: VersionSelectorVersionId,
  ) => Promise<unknown>;
  onArchiveWorkflowVersion?: {
    mutate: (
      params: { workflow_version_id: number; restore?: boolean },
      options?: { onSuccess?: () => void },
    ) => void;
    isPending: boolean;
  };
  onArchiveSkillCollectionVersion?: {
    mutate: (
      params: {
        workspaceId: number;
        collectionId: string;
        skill_collection_version_id: string;
        restore?: boolean;
      },
      options?: { onSuccess?: () => void; onError?: () => void },
    ) => void;
    isPending: boolean;
  };
  onArchiveToolVersion?: {
    mutate: (
      params: {
        workspaceId: number;
        toolId: number;
        versionId: number;
        restore?: boolean;
      },
      options?: { onSuccess?: () => void; onError?: () => void },
    ) => void;
    isPending: boolean;
  };
  refreshVersions?: () => void;
  counts?: Record<number, number>;
  navigateToVersionLink: (versionNumber: number) => string;
  fetchNextPage?: InfiniteQueryObserverBaseResult["fetchNextPage"];
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  className?: string;
  isWorkflow?: boolean;
  entityType?: EntityType;
}

const VersionSelector = ({
  versions,
  showVersionList,
  setShowVersionList,
  onAddReleaseLabel = async () => undefined,
  onRemoveReleaseLabel = async () => undefined,
  onMoveReleaseLabel = async () => undefined,
  onArchiveWorkflowVersion,
  onArchiveSkillCollectionVersion,
  onArchiveToolVersion,
  refreshVersions,
  dynamicReleaseLabelsRegistry = {},
  versionEvaluationScoresRegistry = {},
  counts = {},
  navigateToVersionLink,
  isWorkflow = false,
  entityType,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  className,
}: VersionSelectorProps) => {
  const {
    versionId: routeVersionId,
    versionNumber: routeVersionNumber,
    promptId,
    workflowId,
    collectionId,
    toolId,
  } = useParams();
  const versionNumber = routeVersionNumber ?? routeVersionId;
  const isSkillCollection = entityType === EntityType.SKILL_COLLECTION;
  const isTool = entityType === EntityType.TOOL;
  let hideArchiveModalEntityLabel: "prompt" | "workflow" | "skill collection" | "tool" = "prompt";
  if (isSkillCollection) {
    hideArchiveModalEntityLabel = "skill collection";
  } else if (isTool) {
    hideArchiveModalEntityLabel = "tool";
  } else if (isWorkflow) {
    hideArchiveModalEntityLabel = "workflow";
  }
  const navigate = useNavigate();
  const auth = useAuth();
  const user = useUser();
  const userToken = auth?.userToken ?? "";
  const workspaceId = user?.activeWorkspaceId ?? 0;

  const [isBacktestModalOpen, setIsBacktestModalOpen] = useState(false);
  const [selectedVersionIdForBacktest, setSelectedVersionIdForBacktest] = useState<number | null>(
    null,
  );
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [selectedVersionIdForArchive, setSelectedVersionIdForArchive] =
    useState<VersionSelectorVersionId | null>(null);
  const [archivingVersionId, setArchivingVersionId] = useState<VersionSelectorVersionId | null>(
    null,
  );
  const [expandedArchivedGroups, setExpandedArchivedGroups] = useState<Set<number>>(new Set());

  const backtestMutation = useCreateBacktestWithLogs(userToken);
  const archiveVersionMutation = useArchivePromptVersion();

  const isArchiving =
    archiveVersionMutation.isPending ||
    onArchiveWorkflowVersion?.isPending ||
    onArchiveSkillCollectionVersion?.isPending ||
    onArchiveToolVersion?.isPending ||
    false;

  const allLabels = useMemo(() => versions?.map(v => v.labels).flat() || [], [versions]);

  const resolvedEntityId = (() => {
    if (isSkillCollection) {
      return Number(collectionId);
    }
    if (isTool) {
      return Number(toolId);
    }
    if (isWorkflow) {
      return Number(workflowId);
    }
    return Number(promptId);
  })();
  const resolvedEntityType = entityType ?? (isWorkflow ? EntityType.WORKFLOW : EntityType.PROMPT);
  const location = useLocation();

  // Fetch pending release label moves from changelog
  const { pendingMoves: pendingReleaseLabelMoves, pendingDeletesByLabelId } =
    usePendingReleaseLabelMoves(
      resolvedEntityId,
      resolvedEntityType,
      !!resolvedEntityId && !isSkillCollection,
    );

  // Handler to navigate to changelog and scroll to specific entry
  const handlePendingLabelClick = (changelogId: number) => {
    let basePath: string;
    if (isTool) {
      basePath = `/workspace/${workspaceId}/tool/${toolId}/version/${versionNumber}/changelog`;
    } else if (isWorkflow) {
      basePath = `/workspace/${workspaceId}/workflow/${workflowId}/version/${versionNumber}/changelog`;
    } else {
      basePath = `/workspace/${workspaceId}/prompt/${promptId}/version/${versionNumber}/changelog`;
    }

    const searchParams = new URLSearchParams(location.search);
    searchParams.set("scrollTo", String(changelogId));
    navigate({ pathname: basePath, search: `?${searchParams.toString()}` });
  };

  const { isLoadingMore, loadingRef, tabListRef } = useInfiniteScroll({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    observerKey: showVersionList ? "expanded" : "collapsed",
  });

  const {
    selectedReleaseLabelGroupId,
    isExperimentModalOpen,
    setExperimentModalOpen,
    handleDynamicReleaseLabelClick,
  } = useExperimentModal();

  const { versionIdForABTest, labelsSelectOpen, setLabelsSelectOpen, handleABTestButtonClick } =
    useABTestManagement();

  const handleBacktestButtonClick = (promptVersionId: number) => {
    setSelectedVersionIdForBacktest(promptVersionId);
    setIsBacktestModalOpen(true);
  };

  const handleArchiveButtonClick = (versionId: VersionSelectorVersionId) => {
    setSelectedVersionIdForArchive(versionId);
    setIsArchiveModalOpen(true);
  };

  const handleArchiveConfirm = () => {
    if (selectedVersionIdForArchive !== null) {
      setArchivingVersionId(selectedVersionIdForArchive);
      if (isWorkflow && onArchiveWorkflowVersion) {
        onArchiveWorkflowVersion.mutate(
          { workflow_version_id: Number(selectedVersionIdForArchive) },
          {
            onSuccess: () => {
              setIsArchiveModalOpen(false);
              setSelectedVersionIdForArchive(null);
              setArchivingVersionId(null);
              refreshVersions?.();
            },
          },
        );
      } else if (isSkillCollection && onArchiveSkillCollectionVersion && collectionId) {
        onArchiveSkillCollectionVersion.mutate(
          {
            workspaceId,
            collectionId,
            skill_collection_version_id: String(selectedVersionIdForArchive),
          },
          {
            onSuccess: () => {
              setIsArchiveModalOpen(false);
              setSelectedVersionIdForArchive(null);
              setArchivingVersionId(null);
              refreshVersions?.();
            },
            onError: () => {
              setIsArchiveModalOpen(false);
              setArchivingVersionId(null);
            },
          },
        );
      } else if (isTool && onArchiveToolVersion && resolvedEntityId) {
        onArchiveToolVersion.mutate(
          {
            workspaceId,
            toolId: Number(resolvedEntityId),
            versionId: Number(selectedVersionIdForArchive),
          },
          {
            onSuccess: () => {
              setIsArchiveModalOpen(false);
              setSelectedVersionIdForArchive(null);
              setArchivingVersionId(null);
              refreshVersions?.();
            },
            onError: () => {
              setIsArchiveModalOpen(false);
              setArchivingVersionId(null);
            },
          },
        );
      } else {
        archiveVersionMutation.mutate(
          { prompt_version_id: Number(selectedVersionIdForArchive) },
          {
            onSuccess: () => {
              setIsArchiveModalOpen(false);
              setSelectedVersionIdForArchive(null);
              setArchivingVersionId(null);
              refreshVersions?.();
            },
            onError: () => {
              setIsArchiveModalOpen(false);
              setArchivingVersionId(null);
            },
          },
        );
      }
    }
  };

  const handleUnarchiveButtonClick = (versionId: VersionSelectorVersionId) => {
    setArchivingVersionId(versionId);
    if (isWorkflow && onArchiveWorkflowVersion) {
      onArchiveWorkflowVersion.mutate(
        { workflow_version_id: Number(versionId), restore: true },
        {
          onSuccess: () => {
            setArchivingVersionId(null);
            refreshVersions?.();
          },
        },
      );
    } else if (isSkillCollection && onArchiveSkillCollectionVersion && collectionId) {
      onArchiveSkillCollectionVersion.mutate(
        {
          workspaceId,
          collectionId,
          skill_collection_version_id: String(versionId),
          restore: true,
        },
        {
          onSuccess: () => {
            setArchivingVersionId(null);
            refreshVersions?.();
          },
          onError: () => {
            setArchivingVersionId(null);
          },
        },
      );
    } else if (isTool && onArchiveToolVersion && resolvedEntityId) {
      onArchiveToolVersion.mutate(
        {
          workspaceId,
          toolId: Number(resolvedEntityId),
          versionId: Number(versionId),
          restore: true,
        },
        {
          onSuccess: () => {
            setArchivingVersionId(null);
            refreshVersions?.();
          },
          onError: () => {
            setArchivingVersionId(null);
          },
        },
      );
    } else if (versionId !== null) {
      archiveVersionMutation.mutate(
        { prompt_version_id: Number(versionId), restore: true },
        {
          onSuccess: () => {
            setArchivingVersionId(null);
            refreshVersions?.();
          },
          onError: () => {
            setArchivingVersionId(null);
            // Error handling is done in the hook
          },
        },
      );
    }
  };

  const handleBacktestSubmit = (backtestVersionId: number, logsVersionId: number | null) => {
    backtestMutation.mutate(
      {
        promptVersionId: backtestVersionId,
        workspaceId,
        promptVersionIdForLogs: logsVersionId,
      },
      {
        onSuccess: (res: { success?: boolean; report_id?: string; error?: string }) => {
          setIsBacktestModalOpen(false);
          if (res?.success && res?.report_id) {
            navigate(`/workspace/${workspaceId}/evaluate/reports/${res.report_id}`);
          } else {
            displayErrorToast(res?.error ?? "There's a problem with the backtest request");
          }
        },
        onError: () => {
          setIsBacktestModalOpen(false);
          displayErrorToast("There's a problem with the backtest request");
        },
      },
    );
  };

  const organizationId = user?.activeWorkspace?.organization_id ?? 0;

  const handleDeleteLabel = (label: { id: number; name: string }) => {
    onRemoveReleaseLabel(label.id);
  };

  const {
    isDialogOpen: isConfigDialogOpen,
    setIsDialogOpen: setIsConfigDialogOpen,
    selectedLabel: labelToConfig,
    openConfigurationDialog,
    handleSave: handleSaveLabelConfig,
    handleDelete: handleDeleteLabelConfig,
  } = useLabelConfiguration({
    entityType: resolvedEntityType,
    onSuccess: refreshVersions,
    onDeleteLabel: handleDeleteLabel,
  });

  const {
    isDragging,
    labelMoveConfirmation,
    setLabelMoveConfirmation,
    handleDragStart,
    handleDragEnd,
    executeReleaseMove,
    pendingLabelMoves,
  } = useLabelDragAndDrop(versions, onMoveReleaseLabel);

  const { augmentedVersions } = useOptimisticVersions(versions, pendingLabelMoves);

  useScrollToCurrentVersion({
    activeVersionNumber: versionNumber ? Number(versionNumber) : null,
    versions: versions || [],
    containerRef: tabListRef,
  });

  // Group archived versions and create placeholders
  const versionsWithPlaceholders = useMemo(() => {
    if (!augmentedVersions || augmentedVersions.length === 0) {
      return [];
    }

    const result: {
      type: "version" | "archived-group";
      data: VersionSelectorVersion;
      count: number;
      groupIndex: number;
      archivedVersions: VersionSelectorVersion[];
      id: string;
    }[] = [];
    let archivedGroup: VersionSelectorVersion[] = [];
    let groupIndex = 0;

    augmentedVersions.forEach(version => {
      if (version.archived) {
        archivedGroup.push(version);
      } else {
        // If we have accumulated archived versions, add them as a group
        if (archivedGroup.length > 0) {
          result.push({
            type: "archived-group",
            data: archivedGroup[0],
            count: archivedGroup.length,
            groupIndex,
            archivedVersions: [...archivedGroup],
            id: `archived-group-${archivedGroup[0].id}`,
          });
          groupIndex += 1;
          archivedGroup = [];
        }
        result.push({
          type: "version",
          data: version,
          id: `version-${version.id}`,
          count: 0,
          groupIndex: 0,
          archivedVersions: [],
        });
      }
    });

    // Handle any remaining archived versions at the end
    if (archivedGroup.length > 0) {
      result.push({
        type: "archived-group",
        data: archivedGroup[0],
        count: archivedGroup.length,
        groupIndex,
        archivedVersions: [...archivedGroup],
        id: `archived-group-${archivedGroup[0].id}`,
      });
    }

    return result;
  }, [augmentedVersions]);

  if (!versions) {
    return <VersionSelectorShimmer className={className} />;
  }
  if (!showVersionList) {
    return (
      <>
        <CollapsedVersionRail
          versions={augmentedVersions}
          dynamicReleaseLabelsRegistry={dynamicReleaseLabelsRegistry}
          activeVersionNumber={versionNumber ? Number(versionNumber) : null}
          navigateToVersionLink={navigateToVersionLink}
          onExpand={() => setShowVersionList(true)}
          containerRef={tabListRef}
          loadingRef={loadingRef}
          hasNextPage={hasNextPage}
          isLoadingMore={isLoadingMore}
          className={className}
        />
        <ConfirmationModal
          title={`Are you sure you want to hide this ${hideArchiveModalEntityLabel} version?`}
          MarkdownDescription={
            <>
              {`Version `}
              <b>{`${versions?.find(v => v.id === selectedVersionIdForArchive)?.number}`}</b>
              {` will be hidden from the version list.`}
            </>
          }
          saveButtonVariant="default"
          isOpen={isArchiveModalOpen}
          headerVariant={ConfirmationModalVariant.warning}
          setIsOpen={setIsArchiveModalOpen}
          leaveButtonText="Cancel"
          saveButtonText="Hide Version"
          useBlocker={false}
          handleOnConfirm={handleArchiveConfirm}
          isLoading={isArchiving}
        />
      </>
    );
  }

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
        <Tab.Group>
          <div
            className={cn(
              "relative flex min-w-[300px] max-w-[25%] flex-col rounded-lg border border-gray-200 bg-white",
              className,
            )}
          >
            <div className="flex items-center justify-between gap-x-3 rounded-t-lg border-b border-gray-200 bg-gray-50 px-3 py-1">
              <div className="flex items-center gap-x-1.5">
                <BasicTooltip content="Hide versions">
                  <span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-gray-500 hover:text-gray-700"
                      onClick={() => setShowVersionList(!showVersionList)}
                    >
                      <PanelLeftClose className="h-4 w-4" />
                    </Button>
                  </span>
                </BasicTooltip>
                <span className="text-xs font-medium text-gray-700">Versions</span>
              </div>
              {isDragging && (
                <span className="text-xs font-medium text-blue-600">
                  Drop label on a version to move it
                </span>
              )}
            </div>
            <Tab.List
              className="relative max-h-full min-h-0 flex-1 overflow-y-auto"
              ref={tabListRef}
            >
              {versionsWithPlaceholders.map(item => {
                if (item.type === "archived-group") {
                  const isExpanded = expandedArchivedGroups.has(item.groupIndex);

                  return (
                    <ArchiveGroup
                      key={`archived-${item.id}`}
                      count={item.count}
                      groupIndex={item.groupIndex}
                      archivedVersions={item.archivedVersions}
                      isExpanded={isExpanded}
                      onToggle={() => {
                        setExpandedArchivedGroups(prev => {
                          const next = new Set(prev);
                          if (next.has(item.groupIndex)) {
                            next.delete(item.groupIndex);
                          } else {
                            next.add(item.groupIndex);
                          }
                          return next;
                        });
                      }}
                      versionNumber={versionNumber}
                      augmentedVersions={augmentedVersions}
                      counts={counts}
                      navigateToVersionLink={navigateToVersionLink}
                      onAddReleaseLabel={onAddReleaseLabel}
                      handleDynamicReleaseLabelClick={handleDynamicReleaseLabelClick}
                      dynamicReleaseLabelsRegistry={dynamicReleaseLabelsRegistry}
                      versionEvaluationScoresRegistry={versionEvaluationScoresRegistry}
                      handleABTestButtonClick={(testPromptId, versionId) =>
                        handleABTestButtonClick(testPromptId, versionId, allLabels)
                      }
                      handleBacktestButtonClick={handleBacktestButtonClick}
                      handleArchiveButtonClick={handleArchiveButtonClick}
                      handleUnarchiveButtonClick={handleUnarchiveButtonClick}
                      archivingVersionId={archivingVersionId}
                      isArchiving={isArchiving}
                      promptId={promptId}
                      isWorkflow={isWorkflow}
                      entityType={resolvedEntityType}
                      onConfigureLabel={openConfigurationDialog}
                      allLabels={allLabels}
                      pendingReleaseLabelMoves={pendingReleaseLabelMoves}
                      pendingDeletesByLabelId={pendingDeletesByLabelId}
                      onPendingLabelClick={handlePendingLabelClick}
                    />
                  );
                }

                const version = item.data;
                const isActiveVersion = Number(versionNumber) === version.number;
                const latestNumber = Math.max(...augmentedVersions.map(v => v.number));
                const isLatestVersion = version.number === latestNumber;

                return (
                  <VersionTab
                    key={version.id}
                    version={version}
                    isActiveVersion={isActiveVersion}
                    isLatestVersion={isLatestVersion}
                    latestVersionNumber={latestNumber}
                    versionNumber={versionNumber}
                    counts={counts}
                    navigateToVersionLink={navigateToVersionLink}
                    onAddReleaseLabel={onAddReleaseLabel}
                    handleDynamicReleaseLabelClick={handleDynamicReleaseLabelClick}
                    dynamicReleaseLabelsRegistry={dynamicReleaseLabelsRegistry}
                    versionEvaluationScoresRegistry={versionEvaluationScoresRegistry}
                    handleABTestButtonClick={(pId, versionId) =>
                      handleABTestButtonClick(pId, versionId, allLabels)
                    }
                    handleBacktestButtonClick={handleBacktestButtonClick}
                    handleArchiveButtonClick={handleArchiveButtonClick}
                    handleUnarchiveButtonClick={handleUnarchiveButtonClick}
                    archivingVersionId={archivingVersionId}
                    isArchiving={isArchiving}
                    promptId={promptId}
                    isWorkflow={isWorkflow}
                    entityType={resolvedEntityType}
                    onConfigureLabel={openConfigurationDialog}
                    allLabels={allLabels}
                    pendingReleaseLabelMoves={pendingReleaseLabelMoves}
                    pendingDeletesByLabelId={pendingDeletesByLabelId}
                    onPendingLabelClick={handlePendingLabelClick}
                  />
                );
              })}
              {fetchNextPage && (hasNextPage || isLoadingMore) && (
                <div ref={loadingRef} className="h-10 w-full py-3" style={{ minHeight: "40px" }}>
                  {isLoadingMore && (
                    <div className="flex justify-center py-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500" />
                      <span className="ml-2 text-xs text-gray-500">Loading more versions...</span>
                    </div>
                  )}
                </div>
              )}
            </Tab.List>
          </div>
        </Tab.Group>
      </DragDropContext>
      {!isSkillCollection && isExperimentModalOpen && selectedReleaseLabelGroupId && (
        <ExperimentModal
          promptId={promptId}
          releaseLabelGroupId={selectedReleaseLabelGroupId}
          setOpen={setExperimentModalOpen}
        />
      )}
      <ConfirmationModal
        title="Are you sure you want to move this label?"
        MarkdownDescription={
          <>
            {`Release label `} <b>{`"${labelMoveConfirmation.labelName}"`}</b>
            {` will be moved to version `}
            <b>{`${
              versions?.find(v => v.id === labelMoveConfirmation.targetVersionId)?.number
            }`}</b>
            .
          </>
        }
        saveButtonVariant="default"
        isOpen={labelMoveConfirmation.isOpen}
        headerVariant={ConfirmationModalVariant.warning}
        setIsOpen={isOpen => setLabelMoveConfirmation(prev => ({ ...prev, isOpen }))}
        leaveButtonText="Cancel"
        saveButtonText="Move Label"
        useBlocker={false}
        handleOnConfirm={executeReleaseMove}
      />
      {!isSkillCollection && (
        <LabelsSelect
          promptVersion={{
            prompt_id: Number(promptId),
            version_id: versionIdForABTest!,
          }}
          open={labelsSelectOpen}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          labels={allLabels as any}
          isLoading={false}
          setOpen={setLabelsSelectOpen}
        />
      )}
      {!isSkillCollection && isBacktestModalOpen && selectedVersionIdForBacktest && versions && (
        <BacktestModal
          isOpen={isBacktestModalOpen}
          setOpen={setIsBacktestModalOpen}
          onSubmit={handleBacktestSubmit}
          versions={versions as ListPromptVersion[]}
          selectedVersionId={selectedVersionIdForBacktest}
          isLoading={backtestMutation.isLoading}
        />
      )}
      <ConfirmationModal
        title={`Are you sure you want to hide this ${hideArchiveModalEntityLabel} version?`}
        MarkdownDescription={
          <>
            {`Version `}
            <b>{`${versions?.find(v => v.id === selectedVersionIdForArchive)?.number}`}</b>
            {` will be hidden from the version list.`}
          </>
        }
        saveButtonVariant="default"
        isOpen={isArchiveModalOpen}
        headerVariant={ConfirmationModalVariant.warning}
        setIsOpen={setIsArchiveModalOpen}
        leaveButtonText="Cancel"
        saveButtonText="Hide Version"
        useBlocker={false}
        handleOnConfirm={handleArchiveConfirm}
        isLoading={isArchiving}
      />
      <ReleaseLabelConfigurationDialog
        isOpen={isConfigDialogOpen}
        onOpenChange={setIsConfigDialogOpen}
        label={labelToConfig}
        onSave={handleSaveLabelConfig}
        onDelete={handleDeleteLabelConfig}
        isWorkflow={isWorkflow}
        isSkillCollection={isSkillCollection}
        organizationId={organizationId}
      />
    </>
  );
};

export default VersionSelector;
